import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import toast from "react-hot-toast";
import { fr } from "yup-locales";
import { setLocale } from "yup";
import { useLoginUserMutation } from "@/redux/services/auth/auth.api";
import { LoginFormData } from "@/redux/services/auth/auth.type";
import { useNavigate } from "react-router-dom";
import {
  isInternationalPhone,
  normalizeLoginIdentifier,
  PHONE_ERRORS,
} from "@/lib/phone";
import { SECURITY_ERROR_MESSAGES } from "@/lib/securityErrors";
import { getApiErrorMessage } from "@/lib/apiErrors";

setLocale(fr);

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// The login has no restaurant to read a national number in, so a phone must
// carry its country code (SFAPI-209).
export const loginFormSchema = yup.object().shape({
  emailOrPhone: yup
    .string()
    .transform((value) =>
      typeof value === "string" ? normalizeLoginIdentifier(value) : value
    )
    .required("Veuillez entrer votre email ou numéro de téléphone")
    .test(
      "emailOrPhone",
      "Saisissez un email, ou un numéro au format international avec l'indicatif du pays (ex. +221 77 123 45 67)",
      (value) => {
        return emailRegex.test(value) || isInternationalPhone(value);
      }
    ),
});

// Login-screen wording for these keys (on top of the phone-format and
// account-state texts); others use the shared map.
const LOGIN_ERROR_MESSAGES: Record<string, string> = {
  ...PHONE_ERRORS,
  ...SECURITY_ERROR_MESSAGES,
  "security.error.user.not_found": "Aucun compte n'est associé à cet email",
  "security.error.unauthorized_app_access": "La connexion n'est pas autorisée",
  "security.error.unauthorized_origin": "La connexion n'est pas autorisée",
  "security.error.origin_header_not_found": "La connexion n'est pas autorisée",
};

/** French text for a failed login request; never the server's own text. */
export const loginErrorMessage = (error: unknown): string =>
  getApiErrorMessage(error, "Une erreur inconnue est survenue", {
    messages: LOGIN_ERROR_MESSAGES,
  });

export const useLoginForm = () => {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    reset,
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginFormSchema),
  });

  const [login, { isLoading }] = useLoginUserMutation();
  const navigate = useNavigate();

  const onSubmit = async (data: LoginFormData) => {
    const res = await login(data);

    const isPhone = isInternationalPhone(data.emailOrPhone);

    if ("data" in res) {
      if (!isPhone) {
        toast.success(
          "Un lien a été envoyé à votre adresse e-mail. Veuillez vérifier votre boîte de réception pour accéder à votre compte."
        );
        reset();
      } else {
        navigate("/auth/login_code", {
          state: {
            phone: data?.emailOrPhone,
            requiresPin: res.data?.requiresPin === true,
          },
        });
      }
    } else if ("error" in res) {
      setError("emailOrPhone", { message: loginErrorMessage(res.error) });
    }
  };

  return {
    register,
    errors,
    onSubmit: handleSubmit(onSubmit),
    isLoading,
  };
};
